// Base Imports
import React, { useEffect } from 'react'
import Link from 'next/link'
import type { GetStaticPropsContext } from 'next'
import dynamic from 'next/dynamic'

import { Tab } from '@headlessui/react'
import { Disclosure } from '@headlessui/react'
import classNames from 'classnames'
// Other Imports
import commerce from '@lib/api/commerce'
import {
  HOMEPAGE_SLUG,
  FAQ_PAGE_DEFAULT_SLUG,
} from '@components/utils/constants'
import withDataLayer, { PAGE_TYPES } from '@components/withDataLayer'
import { EVENTS_MAP } from '@components/services/analytics/constants'
import useAnalytics from '@components/services/analytics/useAnalytics'
import { LayoutWithTopStrip } from '@components/common/Layout'
// const Layout = dynamic(() => import('@components/common/Layout/LayoutWithTopStrip'))
import NextHead from 'next/head'
import { useRouter } from 'next/router'
import { PagePropType, getPagePropType } from '@framework/page-props'
import { IPagePropsProvider } from '@framework/contracts/page-props/IPagePropsProvider'
import useEventAnalytics from '@components/services/analytics/useEventAnalytics'
import { AnalyticsEventType } from '@components/services/analytics'
import { IMAGE_ASSET_BASE_URL } from '@framework/utils/constants'
import { useUI } from '@components/ui'
const Loader = dynamic(() => import('@components/ui/LoadingDots'))

export async function getStaticProps({
  preview,
  locale,
  locales,
}: GetStaticPropsContext) {
  const config = { locale, locales }
  const slugsPromise = commerce.getSlugs({ slug: HOMEPAGE_SLUG })
  const slugs = await slugsPromise
  const infraPromise = commerce.getInfra()
  const infra = await infraPromise

  const pagesPromise = commerce.getAllPages({ config, preview })
  const siteInfoPromise = commerce.getSiteInfo({ config, preview })
  const { pages } = await pagesPromise
  const { categories, brands } = await siteInfoPromise

  // // ---- Display data from content entries ----
  // const id = "05905b59-84a7-41a3-b992-5137c11f86f7";
  // const PageContentsPromise = commerce.getPageContent({ id: id, slug: slugs?.slug });
  // const pageContents = await PageContentsPromise;

  // ---- Display data from pages ----
  //const pageId = "7c79d1b2-53e1-4435-a64f-8298446f3546";
  const PageContentsPromise = commerce.getPagePreviewContent({
    id: '', //pageId,
    slug: FAQ_PAGE_DEFAULT_SLUG, //slugs?.slug,
    workingVersion: process.env.NODE_ENV === 'production' ? true : true, // TRUE for preview, FALSE for prod.
    channel: '',
  })

  const pageContents = await PageContentsPromise
  const props: IPagePropsProvider = getPagePropType({ type: PagePropType.COMMON }) as IPagePropsProvider
  const commonPageProps = await props.getPageProps({ cookies: {} })
  return {
    props: {
      ...commonPageProps,
      categories,
      brands,
      pages,
      slugs,
      globalSnippets: infra?.snippets ?? [],
      snippets: slugs?.snippets ?? [],
      pageContents: pageContents ?? {},
    },
    revalidate: 60,
  }
}
const PAGE_TYPE = PAGE_TYPES.Home
function Faq({
  slugs,
  setEntities,
  recordEvent,
  ipAddress,
  pageContents,
}: any) {
  const { PageViewed } = EVENTS_MAP.EVENT_TYPES
  const { recordAnalytics } = useEventAnalytics()
  const { cartItems } = useUI()
  useAnalytics(PageViewed, {
    entity: JSON.stringify({
      id: slugs?.id,
      name: slugs?.name,
      metaTitle: slugs?.metaTitle,
      MetaKeywords: slugs?.metaKeywords,
      MetaDescription: slugs?.metaDescription,
      Slug: slugs?.slug,
      Title: slugs?.title,
      ViewType: slugs?.viewType,
    }),
    entityName: PAGE_TYPE,
    pageTitle: slugs?.title,
    entityType: 'Page',
    entityId: slugs?.id,
    eventType: 'PageViewed',
  })
  const router = useRouter()
  pageContents['metatitle'] = 'How to Place an Order - DaMENSCH FAQ'
  pageContents['metadescription'] =
    "Welcome to DaMENSCH's comprehensive FAQ guide. We understand that a seamless ordering process is essential for our valued customers, and we are here to guide you every step of the way."

  useEffect(()=>{
    recordAnalytics(AnalyticsEventType.OTHER_PAGE, { type: 'other', data: { cartItems } })
  },[])
  return !pageContents ? (
    <>
      <div className="flex w-full text-center flex-con">
        <Loader />
      </div>
    </>
  ) : (
    <>
      {(pageContents?.metatitle ||
        pageContents?.metadescription ||
        pageContents?.metakeywords) && (
        <>
          <NextHead>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1, maximum-scale=1"
            />
            <link
              rel="canonical"
              id="canonical"
              href={
                pageContents?.canonical ||
                'https://www.damensch.com' + router.asPath
              }
            />
            {pageContents?.metatitle && (
              <>
                <title>{pageContents?.metatitle}</title>
                <meta name="title" content={pageContents?.metatitle} />
              </>
            )}

            {pageContents?.metadescription && (
              <>
                <meta
                  name="description"
                  content={pageContents?.metadescription}
                />
              </>
            )}

            {pageContents?.metakeywords && (
              <meta name="keywords" content={pageContents?.metakeywords} />
            )}
            <meta property="og:image" content={pageContents?.image} />

            {pageContents?.metatitle && (
              <>
                <meta
                  property="og:title"
                  content={pageContents?.metatitle}
                  key="ogtitle"
                />
              </>
            )}

            {pageContents?.metadescription && (
              <>
                <meta
                  property="og:description"
                  content={pageContents?.metadescription}
                  key="ogdesc"
                />
              </>
            )}
          </NextHead>
        </>
      )}

      <section className="relative pb-10 text-gray-900">
        <div className="container px-3 mx-auto faq-container sm:px-6">
          <div className="flex flex-col items-start w-full py-6 align-middle sm:flex-row sm:items-center sm:py-14">
            <div className="mb-4 mr-0 image-info-div sm:mr-6 sm:mb-0">
              <img
                placeholder="blur"
                src={`${IMAGE_ASSET_BASE_URL}/icons/faq-image.png`}
                alt="faq-image"
                className="mob-w-45-img"
              />
            </div>
            <div className="">
              <h1 className="text-xl font-bold text-black sm:text-xl">
                Frequently Asked Questions
              </h1>
              <p className="text-14 text-brown-light">
                God helps those who help themselves
              </p>
            </div>
          </div>
          {/* tab section start */}
          <div className="w-full px-0 sm:px-0 tab-img-group">
            <Tab.Group>
              <Tab.List className="block w-100 mob-tab-scroll">
                <Tab
                  key="order-t"
                  className={({ selected }) =>
                    classNames(
                      'w-full rounded-sm tab-btn-panel py-4 text-sm font-medium leading-5 text-blue-700 flex justify-center items-center border-2',
                      'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                      selected
                        ? 'bg-white shadow border-orange tab-active tab-active-panel'
                        : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'
                    )
                  }
                >
                  {pageContents?.faqpanel1?.map(
                    (faqpanel: any, panel1: number) => (
                      <div
                        className="flex flex-col items-center justify-center w-full"
                        key={panel1}
                      >
                        <h2 className="text-xs font-bold text-black sm:text-xs dark:text-black">
                          {faqpanel?.faqpanel1_heading}
                        </h2>
                      </div>
                    )
                  )}
                </Tab>
                <Tab
                  key="order-t"
                  className={({ selected }) =>
                    classNames(
                      'w-full rounded-sm tab-btn-panel py-4 text-sm font-medium leading-5 text-blue-700 flex justify-center items-center border-2',
                      'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                      selected
                        ? 'bg-white shadow border-orange tab-active tab-active-panel'
                        : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'
                    )
                  }
                >
                  {pageContents?.faqpanel2?.map(
                    (faqpanel2: any, panel2: number) => (
                      <div
                        className="flex flex-col items-center justify-center w-full"
                        key={panel2}
                      >
                        <h2 className="text-xs font-bold text-black sm:text-xs dark:text-black">
                          {faqpanel2?.faqpanel2_heading}
                        </h2>
                      </div>
                    )
                  )}
                </Tab>
                <Tab
                  key="order-t"
                  className={({ selected }) =>
                    classNames(
                      'w-full rounded-sm tab-btn-panel py-4 text-sm font-medium leading-5 text-blue-700 flex justify-center items-center border-2',
                      'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                      selected
                        ? 'bg-white shadow border-orange tab-active tab-active-panel'
                        : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'
                    )
                  }
                >
                  {pageContents?.faqpanel3?.map(
                    (faqpanel3: any, panel3: number) => (
                      <div
                        className="flex flex-col items-center justify-center w-full"
                        key={panel3}
                      >
                        <h2 className="text-xs font-bold text-black sm:text-xs dark:text-black">
                          {faqpanel3?.faqpanel3_heading}
                        </h2>
                      </div>
                    )
                  )}
                </Tab>
                <Tab
                  key="order-t"
                  className={({ selected }) =>
                    classNames(
                      'w-full rounded-sm tab-btn-panel py-4 text-sm font-medium leading-5 text-blue-700 flex justify-center items-center border-2',
                      'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                      selected
                        ? 'bg-white shadow border-orange tab-active tab-active-panel'
                        : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'
                    )
                  }
                >
                  {pageContents?.faqpanel4?.map(
                    (faqpanel4: any, panel4: number) => (
                      <div
                        className="flex flex-col items-center justify-center w-full"
                        key={panel4}
                      >
                        <h2 className="text-xs font-bold text-black sm:text-xs dark:text-black">
                          {faqpanel4?.faqpanel4_heading}
                        </h2>
                      </div>
                    )
                  )}
                </Tab>
              </Tab.List>
              <Tab.Panels className="mt-4">
                <Tab.Panel
                  key="1"
                  className={classNames(
                    'rounded-xl bg-white p-3 pl-0',
                    'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2'
                  )}
                >
                  <div className="w-full py-3 mx-auto max-800-w sm:py-6">
                    {pageContents?.faqsorderandtracking?.map(
                      (faqsorderandtrack: any, id: number) => (
                        <div className="w-full" key={id}>
                          <Disclosure>
                            {({ open }) => (
                              <>
                                <Disclosure.Button className="flex items-center justify-between w-full pb-4 mb-4 text-left bg-white border-b rounded-lg text-primary text-14">
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        faqsorderandtrack?.faqsorderandtracking_question,
                                    }}
                                  ></div>
                                  <i
                                    className={`${
                                      !open ? 'hidden-icon' : 'inline-block'
                                    } sprite-icon sprite-nav-minus`}
                                  ></i>
                                  <i
                                    className={`${
                                      !open ? 'inline-block' : 'hidden-icon'
                                    } sprite-icon sprite-nav-plus`}
                                  ></i>
                                </Disclosure.Button>
                                <Disclosure.Panel className="px-4 pt-2 pb-4 pl-0 mb-4 text-sm font-light border-b text-primary">
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        faqsorderandtrack?.faqsorderandtracking_answer,
                                    }}
                                    className="pb-4"
                                  ></div>
                                  {faqsorderandtrack?.faqreturnandexchange_buttontest !=
                                    '' &&
                                    faqsorderandtrack?.faqsorderandtracking_buttonlink !=
                                      '' && (
                                      <div className="">
                                        <Link
                                          legacyBehavior
                                          href={
                                            faqsorderandtrack?.faqsorderandtracking_buttonlink
                                          }
                                        >
                                          <button
                                            type="button"
                                            className="flex items-center btn-basic-property-bigfaq justify-center px-4 py-0 -mr-0.2 text-primary bg-transparent border-2 border-gray-300 rounded-sm hover:text-white sm:px-6 font-semibold border-btn-color"
                                          >
                                            {
                                              faqsorderandtrack?.faqsorderandtracking_buttontest
                                            }
                                          </button>
                                        </Link>
                                      </div>
                                    )}
                                </Disclosure.Panel>
                              </>
                            )}
                          </Disclosure>
                        </div>
                      )
                    )}
                  </div>
                </Tab.Panel>
                <Tab.Panel
                  key="2"
                  className={classNames(
                    'rounded-xl bg-white p-3',
                    'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2'
                  )}
                >
                  <div className="w-full py-3 mx-auto max-800-w sm:py-6">
                    {pageContents?.faqreturnandexchange?.map(
                      (faqreturnandexchange: any, faqreturn: number) => (
                        <div className="w-full" key={faqreturn}>
                          <Disclosure>
                            {({ open }) => (
                              <>
                                <Disclosure.Button className="flex items-center justify-between w-full pb-4 mb-4 text-left bg-white border-b rounded-lg text-primary text-14">
                                  <span>
                                    {
                                      faqreturnandexchange?.faqreturnandexchange_question
                                    }
                                  </span>
                                  <i
                                    className={`${
                                      !open ? 'hidden-icon' : 'inline-block'
                                    } sprite-icon sprite-nav-minus`}
                                  ></i>
                                  <i
                                    className={`${
                                      !open ? 'inline-block' : 'hidden-icon'
                                    } sprite-icon sprite-nav-plus`}
                                  ></i>
                                </Disclosure.Button>
                                <Disclosure.Panel className="px-4 pt-2 pb-4 pl-0 mb-4 text-sm font-light border-b text-primary">
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        faqreturnandexchange?.faqreturnandexchange_answer,
                                    }}
                                    className="pb-4"
                                  ></div>
                                  {faqreturnandexchange?.faqreturnandexchange_buttontest !=
                                    '' && (
                                    <div className="">
                                      <Link
                                        legacyBehavior
                                        href={
                                          faqreturnandexchange?.faqreturnandexchange_buttonlink
                                        }
                                      >
                                        <button
                                          type="button"
                                          className="flex items-center btn-basic-property-bigfaq justify-center px-4 py-0 -mr-0.2 text-primary bg-transparent border-2 border-gray-300 rounded-sm hover:text-white sm:px-6 font-semibold border-btn-color"
                                        >
                                          {
                                            faqreturnandexchange?.faqreturnandexchange_buttontest
                                          }
                                        </button>
                                      </Link>
                                    </div>
                                  )}
                                </Disclosure.Panel>
                              </>
                            )}
                          </Disclosure>
                        </div>
                      )
                    )}
                  </div>
                </Tab.Panel>
                <Tab.Panel
                  key="3"
                  className={classNames(
                    'rounded-xl bg-white p-3',
                    'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2'
                  )}
                >
                  <div className="w-full py-3 mx-auto max-800-w sm:py-6">
                    {pageContents?.faqmyaccount?.map(
                      (faqmyaccount: any, myaccount: number) => (
                        <div className="w-full" key={myaccount}>
                          <Disclosure>
                            {({ open }) => (
                              <>
                                <Disclosure.Button className="flex items-center justify-between w-full pb-4 mb-4 text-left bg-white border-b rounded-lg text-primary text-14">
                                  <span>
                                    {faqmyaccount?.faqmyaccount_question}
                                  </span>
                                  <i
                                    className={`${
                                      !open ? 'hidden-icon' : 'inline-block'
                                    } sprite-icon sprite-nav-minus`}
                                  ></i>
                                  <i
                                    className={`${
                                      !open ? 'inline-block' : 'hidden-icon'
                                    } sprite-icon sprite-nav-plus`}
                                  ></i>
                                </Disclosure.Button>
                                <Disclosure.Panel className="px-4 pt-2 pb-4 pl-0 mb-4 text-sm font-light border-b text-primary">
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: faqmyaccount?.faqmyaccount_answer,
                                    }}
                                    className=""
                                  ></div>
                                </Disclosure.Panel>
                              </>
                            )}
                          </Disclosure>
                        </div>
                      )
                    )}
                  </div>
                </Tab.Panel>
                <Tab.Panel
                  key="4"
                  className={classNames(
                    'rounded-xl bg-white p-3',
                    'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2'
                  )}
                >
                  <div className="w-full py-3 mx-auto max-800-w sm:py-6">
                    {pageContents?.faqwarranty?.map(
                      (faqwarranty: any, warranty: number) => (
                        <div className="w-full" key={warranty}>
                          <Disclosure>
                            {({ open }) => (
                              <>
                                <Disclosure.Button className="flex items-center justify-between w-full pb-4 mb-4 text-left bg-white border-b rounded-lg text-primary text-14">
                                  <span>
                                    {faqwarranty?.faqwarranty_question}
                                  </span>
                                  <i
                                    className={`${
                                      !open ? 'hidden-icon' : 'inline-block'
                                    } sprite-icon sprite-nav-minus`}
                                  ></i>
                                  <i
                                    className={`${
                                      !open ? 'inline-block' : 'hidden-icon'
                                    } sprite-icon sprite-nav-plus`}
                                  ></i>
                                </Disclosure.Button>
                                <Disclosure.Panel className="px-4 pt-2 pb-4 pl-0 mb-4 text-sm font-light border-b text-primary">
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: faqwarranty?.faqwarranty_answer,
                                    }}
                                    className=""
                                  ></div>
                                </Disclosure.Panel>
                              </>
                            )}
                          </Disclosure>
                        </div>
                      )
                    )}
                  </div>
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </div>
          {/* tab section start */}
        </div>
      </section>
    </>
  )
}

Faq.Layout = LayoutWithTopStrip

export default withDataLayer(Faq, PAGE_TYPE, true, LayoutWithTopStrip)
